import React, { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BarChart3, Loader2, RefreshCw, Trash2, ExternalLink, Plus } from "lucide-react";
import { apiError } from "@/lib/apiError";
import { inr, siteColor } from "@/lib/compare";

const OfferItem = ({ o, onSave, onDelete }) => {
  const [price, setPrice] = useState(o.price);
  return (
    <div className="flex items-center gap-2 p-2 rounded-xl bg-primary-50" data-testid={`offer-item-${o.id}`}>
      <span className="w-8 h-8 rounded-lg text-white font-bold flex items-center justify-center shrink-0" style={{ backgroundColor: siteColor(o.site) }}>{o.site.charAt(0)}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-primary-900 truncate">{o.site}</p>
        <a href={o.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-500 truncate flex items-center gap-1"><ExternalLink className="w-3 h-3" />{o.url}</a>
      </div>
      <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} onBlur={() => Number(price) !== o.price && onSave(o.id, Number(price))} className="w-24 h-9" data-testid={`offer-price-input-${o.id}`} />
      <Button size="icon" variant="outline" className="text-red-600 h-9 w-9" onClick={() => onDelete(o.id)} data-testid={`offer-delete-${o.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
};

export const CompareOffersDialog = ({ product, open, onOpenChange, onChanged }) => {
  const { getAuthHeaders } = useAuth();
  const [offers, setOffers] = useState(product?.compare_offers || []);
  const [url, setUrl] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const base = `${API}/admin/products/affiliate/${product?.id}/offers`;
  const headers = { headers: getAuthHeaders() };

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err, "Something went wrong"));
    }
    setBusy(false);
  };

  const add = () => run(async () => {
    const res = await axios.post(base, { url, price: price ? Number(price) : null }, { ...headers, timeout: 90000 });
    setOffers((o) => [...o, res.data.offer]);
    setUrl("");
    setPrice("");
    toast.success(res.data.message);
  });
  const save = (id, p) => run(async () => {
    await axios.put(`${base}/${id}`, { price: p }, headers);
    setOffers((o) => o.map((x) => (x.id === id ? { ...x, price: p } : x)));
    toast.success("Price updated");
  });
  const remove = (id) => run(async () => {
    await axios.delete(`${base}/${id}`, headers);
    setOffers((o) => o.filter((x) => x.id !== id));
  });
  const refresh = () => run(async () => {
    const res = await axios.post(`${base}/refresh`, {}, { ...headers, timeout: 180000 });
    setOffers(res.data.offers);
    res.data.failed.length ? toast.warning(`${res.data.message}. Couldn't read: ${res.data.failed.join(", ")}`) : toast.success(res.data.message);
  });

  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto w-[calc(100%-2rem)] rounded-3xl sm:pr-8" data-testid="compare-offers-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary-600" />Price comparison</DialogTitle></DialogHeader>
        <p className="text-sm text-primary-600 line-clamp-1">{product.title} · M Ventures {inr(product.discounted_price || product.original_price)}</p>
        <div className="space-y-2">
          {offers.length ? offers.map((o) => <OfferItem key={o.id} o={o} onSave={save} onDelete={remove} />) : <p className="text-sm text-primary-400">No other stores yet.</p>}
        </div>
        <div className="space-y-2 border-t pt-4">
          <p className="text-sm font-bold text-primary-900">Add another store</p>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste the same product's link on Amazon, Flipkart..." data-testid="offer-url-input" />
          <div className="flex gap-2">
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Price (leave empty to auto-read)" data-testid="offer-price-new-input" />
            <Button onClick={add} disabled={!url.trim() || busy} className="bg-primary-600 hover:bg-primary-700 shrink-0" data-testid="offer-add-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Add</Button>
          </div>
        </div>
        {offers.length > 0 && (
          <Button variant="outline" onClick={refresh} disabled={busy} data-testid="offer-refresh-btn"><RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />Refresh all prices</Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
